import { useCallback, useEffect, useRef } from 'react'

import { createSite, updateSiteWizard, generatePageData } from '@/api/onboarding'
import {
  createSiteplusFromTemplate,
  buildEarlyGridSitewizardCreatePayload,
  buildPagesFromCheckedSections,
} from '@/helpers/siteplusBuilder'
import {
  writeGridOnboardingSession,
  normalizeGridAiPageData,
} from '@/helpers/gridOnboardingSessionHelper'
import { useTemplateContext } from '@/context/TemplateProvider'
import useOnboardingContext from '@/hooks/useOnboardingContext'

/**
 * Grid-only: create/update sitewizard and fetch AI page data after leaving the style step
 * or when the template changes (handled via leave-style with existing wizard id).
 */
const useGridSitewizardPipeline = () => {
  const ctx = useOnboardingContext()
  const { template } = useTemplateContext()
  const ctxRef = useRef(ctx)
  const templateRef = useRef(template)
  const sequenceRef = useRef(0)

  useEffect(() => {
    ctxRef.current = ctx
  })
  useEffect(() => {
    templateRef.current = template
  })

  const runGridPipelineAfterLeaveStyle = useCallback(async () => {
    const c = ctxRef.current
    const tmpl = templateRef.current
    if (!c.isGrid || !c.accessToken || !c.selectedTemplate?.id) return

    const templateId = c.selectedTemplate.id
    if (c.gridSitewizardId && templateId === c.lastGridGeneratedTemplateId) {
      return
    }

    const seq = ++sequenceRef.current
    c.setGridGenerationStatus('pending')

    try {
      const pages = buildPagesFromCheckedSections(c.checkedSortedSections)
      const fullUpdatePayload = createSiteplusFromTemplate({
        templateCategoryData: c.templateCategoryData,
        websiteDetails: c.websiteDetails,
        selectedFont: c.selectedFont ?? {},
        contentStructureData: c.contentStructureData,
        selectedTemplate: c.selectedTemplate,
        template: tmpl,
        selectedPalette: c.selectedPalette ?? {},
        productInstanceId: c.productInstanceId,
        sections: c.sections ?? {},
        accessToken: c.accessToken,
        isGrid: true,
        recommendedApps: c.recommendedApps,
        selectedTone: c.selectedTone,
        layout: c.layout,
        earlyGeneration: true,
        pages,
      })

      const earlyCreatePayload = buildEarlyGridSitewizardCreatePayload({
        accessToken: c.accessToken,
        productInstanceId: c.productInstanceId,
        templateCategoryData: c.templateCategoryData,
        websiteDetails: c.websiteDetails,
        selectedTemplate: c.selectedTemplate,
        recommendedApps: c.recommendedApps,
        layout: c.layout,
        template: tmpl,
      })

      let sid = c.gridSitewizardId
      if (!sid) {
        const res = await createSite(earlyCreatePayload)
        sid = res._id
        if (seq !== sequenceRef.current) return
        c.setGridSitewizardId(sid)
      } else {
        await updateSiteWizard(sid, fullUpdatePayload)
        if (seq !== sequenceRef.current) return
      }

      const raw = await generatePageData({
        accessToken: c.accessToken,
        sitewizardId: sid,
      })
      if (seq !== sequenceRef.current) return

      const normalized = normalizeGridAiPageData(raw)
      c.setGridAiPageData(normalized)
      c.setLastGridGeneratedTemplateId(templateId)
      c.setGridGenerationStatus('ready')
      writeGridOnboardingSession(c.productInstanceId, {
        sitewizardId: sid,
        aiPageData: normalized,
        lastGeneratedTemplateId: templateId,
      })
    } catch (err) {
      if (seq !== sequenceRef.current) return
      console.error('Grid sitewizard pipeline failed', err)
      c.setGridGenerationStatus('error')
    }
  }, [])

  return { runGridPipelineAfterLeaveStyle }
}

export default useGridSitewizardPipeline
